"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.prefab = exports.usePrefab = exports.PrefabTestProvider = exports.PrefabProvider = void 0;
const react_1 = __importDefault(require("react"));
const prefab_cloud_js_1 = require("@prefab-cloud/prefab-cloud-js");
Object.defineProperty(exports, "prefab", { enumerable: true, get: function () { return prefab_cloud_js_1.prefab; } });
const version_1 = __importDefault(require("./version"));
const defaultContext = {
    get: (_) => undefined,
    getDuration: (_) => undefined,
    isEnabled: (_) => false,
    keys: [],
    loading: true,
    contextAttributes: {},
    prefab: prefab_cloud_js_1.prefab,
    settings: {},
};
const PrefabContext = react_1.default.createContext(defaultContext);
const usePrefab = () => react_1.default.useContext(PrefabContext);
exports.usePrefab = usePrefab;
let globalPrefabIsTaken = false;
const assignPrefabClient = () => {
    if (globalPrefabIsTaken) {
        return new prefab_cloud_js_1.Prefab();
    }
    globalPrefabIsTaken = true;
    return prefab_cloud_js_1.prefab;
};
function PrefabProvider({ apiKey, contextAttributes = {}, onError = () => { }, children, timeout, endpoints, apiEndpoint, pollInterval, afterEvaluationCallback = undefined, collectEvaluationSummaries, collectLoggerNames, collectContextMode, }) {
    const settings = {
        apiKey,
        endpoints,
        apiEndpoint,
        timeout,
        pollInterval,
        onError,
        afterEvaluationCallback,
        collectEvaluationSummaries,
        collectLoggerNames,
        collectContextMode,
    };
    // We use this state to prevent a double-init when useEffect fires due to
    // StrictMode
    const mostRecentlyLoadingContextKey = react_1.default.useRef(undefined);
    // We use this state to pass the loading state to the Provider (updating
    // currentLoadingContextKey won't trigger an update)
    const [loading, setLoading] = react_1.default.useState(true);
    // Here we track the current identity so we can reload our config when it
    // changes
    const [loadedContextKey, setLoadedContextKey] = react_1.default.useState("");
    const prefabClient = react_1.default.useMemo(() => assignPrefabClient(), []);
    if (Object.keys(contextAttributes).length === 0) {
        // eslint-disable-next-line no-console
        console.warn("PrefabProvider: You haven't passed any contextAttributes. See https://docs.prefab.cloud/docs/sdks/react#using-context");
    }
    const context = new prefab_cloud_js_1.Context(contextAttributes);
    const contextKey = context.encode();
    react_1.default.useEffect(() => {
        if (mostRecentlyLoadingContextKey.current === contextKey) {
            return;
        }
        setLoading(true);
        if (mostRecentlyLoadingContextKey.current === undefined) {
            mostRecentlyLoadingContextKey.current = contextKey;
            if (!apiKey) {
                throw new Error("PrefabProvider: apiKey is required");
            }
            const initOptions = Object.assign(Object.assign({ context }, settings), { apiKey, clientNameString: "prefab-cloud-react", clientVersionString: version_1.default });
            prefabClient
                .init(initOptions)
                .then(() => {
                setLoadedContextKey(contextKey);
                setLoading(false);
                if (pollInterval) {
                    prefabClient.poll({ frequencyInMs: pollInterval });
                }
            })
                .catch((reason) => {
                setLoading(false);
                onError(reason);
            });
        }
        else {
            mostRecentlyLoadingContextKey.current = contextKey;
            prefabClient
                .updateContext(context)
                .then(() => {
                setLoadedContextKey(contextKey);
                setLoading(false);
            })
                .catch((reason) => {
                setLoading(false);
                onError(reason);
            });
        }
    }, [
        apiKey,
        loadedContextKey,
        contextKey,
        loading,
        setLoading,
        onError,
        prefabClient.instanceHash,
    ]);
    const value = react_1.default.useMemo(() => ({
        isEnabled: prefabClient.isEnabled.bind(prefabClient),
        contextAttributes,
        get: prefabClient.get.bind(prefabClient),
        getDuration: prefabClient.getDuration.bind(prefabClient),
        keys: Object.keys(prefabClient.configs),
        prefab: prefabClient,
        loading,
        settings,
    }), [loadedContextKey, loading, prefabClient.instanceHash, settings]);
    return react_1.default.createElement(PrefabContext.Provider, { value: value }, children);
}
exports.PrefabProvider = PrefabProvider;
function PrefabTestProvider({ apiKey, config, children }) {
    const get = (key) => config[key];
    const getDuration = (key) => config[key];
    const isEnabled = (key) => !!get(key);
    const value = react_1.default.useMemo(() => {
        const prefabClient = assignPrefabClient();
        prefabClient.get = get;
        prefabClient.getDuration = getDuration;
        prefabClient.isEnabled = isEnabled;
        return {
            isEnabled,
            contextAttributes: config.contextAttributes,
            get,
            getDuration,
            loading: false,
            prefab: prefabClient,
            keys: Object.keys(config),
            settings: { apiKey: apiKey !== null && apiKey !== void 0 ? apiKey : "fake-api-key-via-the-test-provider" },
        };
    }, [config]);
    return react_1.default.createElement(PrefabContext.Provider, { value: value }, children);
}
exports.PrefabTestProvider = PrefabTestProvider;
